import { useState, useEffect, useRef } from 'react'
import {
    Alert,
    Box,
    Button,
    TableContainer,
    Table,
    TableHead,
    TableCell,
    TableRow,
    Paper,
    TableBody,
    Tabs,
    Tab,
    Typography,
    Link,
    TextField,
    Divider
} from '@mui/material';
import useTsPaginator from 'ts-paginator';
import Paginator from '../../../layout/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import { getUploadedDocuments, postUploadedDocuments } from '../../../../actions/tigercat-burns/tigercat-base-actions';

const fileTabs = [
    'Operational Files',
    'Project Files',
    'Reports',
    'Invoices',
    'Carbon Certification',
    'Additional Files',
]

const fileTabTypes = {
    0: 'Operational Files',
    1: 'Project Files',
    2: 'Reports',
    3: 'Invoices',
    4: 'Carbon Certification',
    5: 'Additional Files',
};

const defaultFormState = {
    file: null,
    description: ''
}

export default function DocumentUploads() {
    const dispatch = useDispatch()
    const fileInputRef = useRef(null)
    const [formState, setFormState] = useState(defaultFormState);
    const [isLoading, setIsLoading] = useState(false);
    const [formErrors, setFormErrors] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const [selectedFileType, setSelectedFileType] = useState(fileTabTypes[selectedTab])
    const tableRecordCount = useSelector((state) => (state.tigercatBurnsBaseReducer.uploadedDocuments?.pagination?.total_records))
    const {
        totalRecordCount,
        rowsPerPage,
        currentPage,
        _determinePaginationMessage,
        _determinePaginationDisabledState,
        // _determinePaginationPages,
        _determineRowsPerPageOptions,
        _handleChangeTotalRecordCount,
        _handleChangeRowsPerPage,
        _handleChangePage,
    } = useTsPaginator(+tableRecordCount || 0, 0);
    const tableData = useSelector((state) => state.tigercatBurnsBaseReducer.uploadedDocuments?.data)

    useEffect(() => {
        handleFormErrorReset()
        dispatch(getUploadedDocuments(rowsPerPage, currentPage, selectedFileType))
        // setTableState(tableData)
    }, [dispatch, rowsPerPage, currentPage, selectedFileType]);

    useEffect(() => {
        handleResetForm()
        setSelectedFileType(fileTabTypes[selectedTab])
    }, [selectedTab])

    useEffect(() => {
        _handleChangePage(0);
        _handleChangeRowsPerPage(10)
        _handleChangeTotalRecordCount(+tableRecordCount);
    }, [tableRecordCount]);

    const setData = (event) => {
        const { name, type } = event.target;
        let value = event.target.value

        if (type === 'file') {
            value = event.target.files[0]
        }

        setFormState(prevState => ({
            ...prevState,
            [name]: value,
        }));
    }

    const handleTabChange = (event, newValue) => {
        setSelectedFileType(fileTabTypes[newValue])
        setSelectedTab(newValue);
    };

    const handleFormErrorReset = () => {
        setFormErrors(null);
    };

    const handleResetForm = () => {
        setFormState(defaultFormState)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const onSubmit = async () => {
        try {
            setIsLoading(true)
            validateUploadForm()
            const formData = createFormData()
            await dispatch(postUploadedDocuments(formData, rowsPerPage, currentPage, selectedFileType))
            await dispatch(getUploadedDocuments(rowsPerPage, currentPage, selectedFileType))
            handleResetForm()
        } catch (error) {
            setFormErrors(error.message)
        } finally {
            setIsLoading(false)
        }
    }

    function validateUploadForm() {
        if (formState.file === null) {
            throw Error('Please upload a file before submitting.')
        }
    }

    function createFormData() {
        const formData = new FormData()
        Object.entries(formState).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value)
            }
        })
        return formData
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 3 }}>
                <Typography variant='h5'>Document Management</Typography>
                <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                >
                    {fileTabs.map((fileTab, index) => (
                        <Tab key={index} label={fileTab} />
                    ))}
                </Tabs>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
                    <input
                        id='file-upload-input'
                        style={{ display: 'none' }}
                        name={'file'}
                        ref={fileInputRef}
                        onChange={(event) => {
                            setData(event)
                        }}
                        type="file"
                    />
                    <label htmlFor='file-upload-input'>
                        <Button variant="contained" component="span">
                            Upload {selectedFileType}
                        </Button>
                    </label>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                        {/* <Typography>Current File:</Typography> */}
                        {
                            formState.file && formState.file.name &&
                            <Link
                                target='_blank'
                                href={URL.createObjectURL(formState.file)}
                                name='file'
                            >
                                {formState.file.name}
                            </Link>

                        }
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, justifyContent: 'center', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'center', alignItems: 'flex-start' }}>
                        <Typography sx={{ alignSelf: 'center' }}>Description (Optional):</Typography>
                        <TextField
                            size='small'
                            disabled={!formState.file}
                            name='description'
                            value={formState.description || ''}
                            onChange={(event) => setData(event)}
                            placeholder={formState.file ? 'File Description' : '[No File Selected]'}
                            sx={{ width: 400 }}
                            inputProps={{
                                sx: {
                                    '&::placeholder': {
                                        fontStyle: 'italic'
                                    }
                                }
                            }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Button
                            variant='contained'
                            sx={{ width: 'fit-content' }}
                            onClick={() => onSubmit()}
                            disabled={isLoading}
                        >{isLoading ? `Submitting ${selectedFileType}...` : `Submit ${selectedFileType}`}</Button>
                        {formErrors &&
                            <Alert severity='error' sx={{ width: 'fit-content' }}>
                                {formErrors}
                            </Alert>}
                    </Box>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <UploadedDocumentsTable tableData={tableData} selectedFileType={selectedFileType} />
                <Paginator
                    totalRecordCount={totalRecordCount}
                    rowsPerPage={rowsPerPage}
                    currentPage={currentPage}
                    handleChangePage={_handleChangePage}
                    handleChangeRowsPerPage={_handleChangeRowsPerPage}
                    determinePaginationMessage={_determinePaginationMessage}
                    determinePaginationDisabledState={_determinePaginationDisabledState}
                    determineRowsPerPageOptions={_determineRowsPerPageOptions}
                />
            </Box>
        </Box>
    )
}

const uploadedDocumentsTableColumns = [
    'File Name',
    'File Type',
    'Description',
    'Date Uploaded',
]

function UploadedDocumentsTable({ tableData, selectedFileType }) {
    return tableData?.length === 0
        ? <NoDocuments selectedFileType={selectedFileType} />
        : <TableContainer component={Paper}>
            <Table aria-label="simple table">
                <TableHead>
                    <TableRow>
                        {uploadedDocumentsTableColumns.map((column, index) => (
                            <TableCell
                                key={index}
                                align={column === 'Date Uploaded' ? 'center' : column === 'Description' ? 'center' : 'left'}
                                sx={{ fontSize: 20, fontWeight: 'bold' }}>
                                {column}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableData && tableData?.map((row, index) => (
                        <TableRow key={index}>
                            <TableCell size="small" align="left">
                                <Link
                                    href={row.file_url}
                                    target='_blank'
                                >
                                    {row.file_name}
                                </Link>
                            </TableCell>
                            <TableCell size="small" align="left">
                                {row.file_type}
                            </TableCell>
                            <TableCell size="small" align="center">
                                {row.description}
                            </TableCell>
                            <TableCell size="small" align="center">
                                {row.created_at ? new Date(row.created_at).toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                }) : ''}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody >
            </Table >
        </TableContainer >
}

function NoDocuments({ selectedFileType }) {
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignContent: 'center',
            width: '100%',
            height: 100,
            borderRadius: '3px',
            boxShadow: '0px 1px 3px rgba(0,0,0,0.2), 0px 1px 1px rgba(0,0,0,0.14), 0px 2px 1px rgba(0,0,0,0.12)',
        }}>
            <Box sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignContent: 'center',
                gap: 2
            }}>
                <Divider sx={{ pb: 1 }} />
                <Alert severity='info'
                    sx={{
                        width: 'fit-content',
                        justifySelf: 'center',
                        alignSelf: 'center',
                    }}>No {selectedFileType} data uploaded.</Alert>
            </Box>
        </Box>
    )
}